import React, { useState, useEffect } from 'react';
import axios from '../../api/axios';
import Layout from '../../common/components/Navbar';
import { notifications } from '@mantine/notifications';
import {
  Box,
  Button,
  Text,
  Card,
  Group,
  TextInput,
  Select,
  Badge,
  Modal,
  rem,
  Grid,
  Tabs,
  Divider,
  useMantineTheme,
  ScrollArea
} from '@mantine/core';
import { DatePickerInput } from '@mantine/dates';
import '@mantine/dates/styles.css';
import {
  IconPlus, IconTrash, IconSpeakerphone, IconRotate2,
  IconAlertCircle, IconMoodEmpty, IconArchive, IconCalendar, IconUsers
} from '@tabler/icons-react';

const Announcements = () => {
  const theme = useMantineTheme();
  const [announcements, setAnnouncements] = useState([]);
  
  // View States
  const [tabValue, setTabValue] = useState('active'); 
  
  // Post/Reuse Modal States
  const [open, setOpen] = useState(false);
  const [reuseOpen, setReuseOpen] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  
  // Form State
  const [formData, setFormData] = useState({
    title: '',
    details: '',
    priority: 'normal',
    targetAudience: 'all', // Default 'all'
    expirationDate: null
  });
  const [reuseDate, setReuseDate] = useState(null);

  // View Details Modal State
  const [detailsModalOpen, setDetailsModalOpen] = useState(false);
  const [selectedAnnouncement, setSelectedAnnouncement] = useState(null);

  const user = JSON.parse(localStorage.getItem('user'));
  // Determine if user has edit rights
  const isAdmin = user?.role === 'admin' || user?.role === 'staff' || user?.role === 'super_admin';

  useEffect(() => {
    fetchAnnouncements();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tabValue]);

  const fetchAnnouncements = async () => {
    try {
      const config = { headers: { Authorization: `Bearer ${user.token}` } };
      // Admin accesses the Protected route which returns ALL announcements
      const endpoint = tabValue === 'active' ? '/api/announcements' : '/api/announcements/archived';
      const { data } = await axios.get(endpoint, config);
      setAnnouncements(data);
    } catch (error) {
      notifications.show({ title: 'Error', message: 'Failed to fetch announcements.', color: 'red' });
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };
  
  const handleSelectChange = (name, value) => {
      setFormData({ ...formData, [name]: value });
  };

  // --- ACTION HANDLERS ---

  const handleCardClick = (announcement) => {
    setSelectedAnnouncement(announcement);
    setDetailsModalOpen(true);
  };

  const handleSubmit = async () => {
    if (!formData.title || !formData.details || !formData.expirationDate || !formData.targetAudience) {
        notifications.show({ title: 'Validation Error', message: 'Please fill in all required fields.', color: 'yellow' });
        return;
    }
      
    try {
      const config = { headers: { Authorization: `Bearer ${user.token}` } };
      await axios.post('/api/announcements', formData, config);
      setOpen(false);
      setFormData({ title: '', details: '', priority: 'normal', targetAudience: 'all', expirationDate: null });
      fetchAnnouncements();
      notifications.show({ title: 'Success', message: 'Announcement posted!', color: 'green' });
    } catch (error) {
      notifications.show({ title: 'Error', message: 'Failed to post announcement.', color: 'red' });
    }
  };

  const handleReuseClick = (e, id) => {
    e.stopPropagation(); // Prevent card click
    setSelectedId(id);
    setReuseDate(null);
    setReuseOpen(true);
  };

  const submitReuse = async () => {
    if (!reuseDate) return;
      
    try {
      const config = { headers: { Authorization: `Bearer ${user.token}` } };
      await axios.put(`/api/announcements/${selectedId}/reuse`, { expirationDate: reuseDate }, config);
      setReuseOpen(false);
      setTabValue('active'); 
      fetchAnnouncements();
      notifications.show({ title: 'Success', message: 'Announcement reposted!', color: 'green' });
    } catch (error) {
      notifications.show({ title: 'Error', message: 'Failed to repost announcement.', color: 'red' });
    }
  };

  // LOGIC: Archive if active
  const handleArchive = async (e, id) => {
    e.stopPropagation();
    if (!window.confirm('Archive this announcement? It will move to the inactive list.')) return;

    try {
        const config = { headers: { Authorization: `Bearer ${user.token}` } };
        await axios.put(`/api/announcements/${id}/archive`, {}, config);
        fetchAnnouncements();
        notifications.show({ title: 'Success', message: 'Announcement archived.', color: 'blue' });
    } catch (error) {
        notifications.show({ title: 'Error', message: 'Failed to archive.', color: 'red' });
    }
  };

  // LOGIC: Delete if already archived
  const handleDelete = async (e, id) => {
    e.stopPropagation(); 
    if (!window.confirm('Delete this announcement permanently? This cannot be undone.')) return;
      
    try {
      const config = { headers: { Authorization: `Bearer ${user.token}` } };
      await axios.delete(`/api/announcements/${id}`, config);
      fetchAnnouncements();
      notifications.show({ title: 'Deleted', message: 'Announcement permanently deleted.', color: 'red' });
    } catch (error) {
      notifications.show({ title: 'Error', message: 'Failed to delete announcement.', color: 'red' });
    }
  };

  const getPriorityColor = (priority) => {
    if (priority === 'urgent') return 'red';
    if (priority === 'high') return 'orange';
    return 'blue';
  };

  const getAudienceBadgeColor = (audience) => {
      if (audience === 'admins') return 'grape';
      if (audience === 'homeowners') return 'cyan';
      return 'gray';
  };

  return (
    <Layout>
      <Group justify="space-between" mb="lg">
        <Text component="h1" fz="xl" fw={700}>Announcements</Text>
        {isAdmin && (
          <Button 
            variant="filled" 
            color="green"
            leftSection={<IconPlus style={{ width: rem(18) }} />} 
            onClick={() => setOpen(true)}
          >
            Post Announcement
          </Button>
        )}
      </Group>

      <Tabs defaultValue="active" value={tabValue} onChange={setTabValue} mb="md" color="green">
        <Tabs.List>
          <Tabs.Tab value="active" leftSection={<IconSpeakerphone size={16} />}>Active / Recent</Tabs.Tab>
          {isAdmin && <Tabs.Tab value="archived" leftSection={<IconArchive size={16} />}>Archived / Past</Tabs.Tab>}
        </Tabs.List>
        
        <Tabs.Panel value={tabValue} pt="md">
            <Box style={{ display: 'flex', flexDirection: 'column', gap: rem(15) }}>
                {announcements.length > 0 ? (
                    announcements.map((announcement) => (
                        <Card 
                            key={announcement._id} 
                            shadow="sm" 
                            padding="lg" 
                            radius="md" 
                            withBorder
                            onClick={() => handleCardClick(announcement)}
                            style={{ cursor: 'pointer', transition: 'transform 0.2s', '&:hover': { transform: 'scale(1.01)' } }}
                        >
                            <Group justify="space-between" align="flex-start" mb="xs">
                                <Group gap="sm">
                                    <IconAlertCircle style={{ width: rem(24), height: rem(24) }} color={theme.colors[getPriorityColor(announcement.priority)][6]} />
                                    <Box>
                                        <Text fz="lg" fw={700} lh={1.2}>{announcement.title}</Text>
                                        <Group gap={6}>
                                            <Text fz="xs" c="dimmed">
                                                Posted: {new Date(announcement.createdAt).toLocaleDateString()}
                                            </Text>
                                            <Text fz="xs" c="dimmed">•</Text>
                                            <Badge 
                                                variant="light" 
                                                size="xs" 
                                                color={getAudienceBadgeColor(announcement.targetAudience)}
                                            >
                                                For: {announcement.targetAudience?.toUpperCase() || 'ALL'}
                                            </Badge>
                                        </Group>
                                    </Box>
                                </Group>
                                <Badge 
                                    color={getPriorityColor(announcement.priority)} 
                                    variant="light" 
                                    tt="uppercase"
                                >
                                    {announcement.priority}
                                </Badge>
                            </Group>

                            <Text fz="sm" lineClamp={2} c="dimmed" mb="md">
                                {announcement.details}
                            </Text>

                            <Divider mb="xs" />

                            <Group justify="space-between" align="center">
                                <Text fz="xs" c="dimmed">
                                    Expires: {new Date(announcement.expirationDate).toLocaleDateString()}
                                </Text>

                                {isAdmin && (
                                    <Group gap="xs">
                                        {/* LOGIC CHANGE: If Active -> Archive Button. If Archived -> Reuse & Delete Buttons */}
                                        {tabValue === 'active' ? (
                                            <Button 
                                                variant="subtle" 
                                                size="compact-xs" 
                                                color="orange"
                                                leftSection={<IconArchive style={{ width: rem(14) }} />}
                                                onClick={(e) => handleArchive(e, announcement._id)}
                                            >
                                                Archive
                                            </Button>
                                        ) : (
                                            <>
                                                <Button 
                                                    variant="light" 
                                                    size="compact-xs" 
                                                    leftSection={<IconRotate2 style={{ width: rem(14) }} />} 
                                                    onClick={(e) => handleReuseClick(e, announcement._id)}
                                                >
                                                    Reuse
                                                </Button>
                                                <Button 
                                                    variant="subtle" 
                                                    size="compact-xs" 
                                                    color="red"
                                                    leftSection={<IconTrash style={{ width: rem(14) }} />}
                                                    onClick={(e) => handleDelete(e, announcement._id)}
                                                >
                                                    Delete
                                                </Button>
                                            </>
                                        )}
                                    </Group>
                                )}
                            </Group>
                        </Card>
                    ))
                ) : (
                    <Card shadow="sm" padding="xl" radius="md" withBorder style={{ minHeight: rem(150) }}>
                        <Group gap="xs" justify="center">
                            <IconMoodEmpty style={{ width: rem(24) }} color={theme.colors.gray[5]} />
                            <Text c="dimmed" fz="lg" ta="center" mt="sm">
                                No announcements found.
                            </Text>
                        </Group>
                    </Card>
                )}
            </Box>
        </Tabs.Panel>
      </Tabs>

      {/* VIEW DETAILS MODAL */}
      <Modal 
        opened={detailsModalOpen} 
        onClose={() => setDetailsModalOpen(false)} 
        title={<Text fw={700} size="lg">{selectedAnnouncement?.title}</Text>}
        centered
        size="lg"
      >
        {selectedAnnouncement && (
            <Box>
                <Group mb="md">
                    <Badge 
                        color={getPriorityColor(selectedAnnouncement.priority)} 
                        size="lg"
                    >
                        {selectedAnnouncement.priority.toUpperCase()}
                    </Badge>
                    <Badge 
                        color={getAudienceBadgeColor(selectedAnnouncement.targetAudience)} 
                        variant="dot"
                        size="lg"
                    >
                        To: {selectedAnnouncement.targetAudience?.toUpperCase()}
                    </Badge>
                    <Text c="dimmed" size="sm">
                        {new Date(selectedAnnouncement.createdAt).toLocaleDateString()}
                    </Text>
                </Group>
                
                <ScrollArea.Autosize maxHeight={400}>
                    <Text style={{ whiteSpace: 'pre-wrap', lineHeight: 1.6 }}>
                        {selectedAnnouncement.details}
                    </Text>
                </ScrollArea.Autosize>
                
                <Divider my="md" />
                <Group justify="flex-end">
                    <Button variant="default" onClick={() => setDetailsModalOpen(false)}>Close</Button>
                </Group>
            </Box>
        )}
      </Modal>

      {/* Post Modal */}
      <Modal opened={open} onClose={() => setOpen(false)} title={<Text fw={600}>Post New Announcement</Text>} centered>
        <Box component="form" onSubmit={(e) => { e.preventDefault(); handleSubmit(); }} style={{ display: 'flex', flexDirection: 'column', gap: rem(15) }}>
            <TextInput required label="Title" name="title" value={formData.title} onChange={handleChange} />
            <Grid gutter="sm">
              <Grid.Col span={6}>
                <Select
                  required
                  label="Priority"
                  name="priority"
                  value={formData.priority}
                  onChange={(value) => handleSelectChange('priority', value)}
                  data={[
                    { value: 'normal', label: 'Normal' },
                    { value: 'high', label: 'High' },
                    { value: 'urgent', label: 'Urgent' },
                  ]}
                />
              </Grid.Col>
              <Grid.Col span={6}>
                <Select
                  required
                  label="Target Audience"
                  name="targetAudience"
                  value={formData.targetAudience}
                  onChange={(value) => handleSelectChange('targetAudience', value)}
                  leftSection={<IconUsers size={16} />}
                  data={[
                    { value: 'all', label: 'All Residents & Admin' },
                    { value: 'homeowners', label: 'Homeowners Only' },
                    { value: 'admins', label: 'Admins Only' },
                  ]}
                />
              </Grid.Col>
              <Grid.Col span={12}>
                <DatePickerInput
                  required
                  label="Expiration Date"
                  placeholder="Pick date"
                  value={formData.expirationDate}
                  onChange={(val) => setFormData({...formData, expirationDate: val})}
                  minDate={new Date()}
                  leftSection={<IconCalendar size={16}/>}
                />
              </Grid.Col>
            </Grid>
            <TextInput 
              required
              label="Details" 
              name="details" 
              component="textarea"
              rows={6}
              value={formData.details} 
              onChange={handleChange} 
            />
            <Group justify="flex-end" mt="md">
                <Button variant="default" onClick={() => setOpen(false)}>Cancel</Button>
                <Button variant="filled" color="green" type="submit">Post</Button>
            </Group>
        </Box>
      </Modal>

      {/* Reuse Modal */}
      <Modal opened={reuseOpen} onClose={() => setReuseOpen(false)} title={<Text fw={600}>Reuse Announcement</Text>} centered size="sm">
          <Text fz="sm" mb="md">Set a new expiration date for this announcement.</Text>
          <Box component="form" onSubmit={(e) => { e.preventDefault(); submitReuse(); }} style={{ display: 'flex', flexDirection: 'column', gap: rem(15) }}>
            <DatePickerInput
                  required
                  label="New Expiration Date"
                  placeholder="Pick date"
                  value={reuseDate}
                  onChange={setReuseDate}
                  minDate={new Date()}
                  leftSection={<IconCalendar size={16}/>}
            />
            <Group justify="flex-end" mt="md">
                <Button variant="default" onClick={() => setReuseOpen(false)}>Cancel</Button>
                <Button variant="filled" color="green" type="submit">Repost</Button>
            </Group>
          </Box>
      </Modal>
    </Layout>
  );
};

export default Announcements;